import { setAuthToken } from '../lib/api'
import { createHttpError, getErrorStatusCode } from '../lib/auth-error-handler'
import * as authService from '../lib/auth-service'
import { isDealerAccessDenied, redirectToDealerAccess } from '../lib/dealer-access'
import { consumeLoginRedirectTo } from '../lib/login-redirect'
import {
  PROXY_SESSION_ENDED_PATH,
  clearAuthData,
  getStoredRefreshToken,
  getStoredToken,
  getStoredUser,
  getTokenExpiresAt,
  isProxySession,
  storeAuthData,
} from '../lib/auth-storage'
import { logger } from '../lib/logger'
import { extractUserFacingMessage } from './sdkErrorUtils'
import { i18n } from '../shared/i18n'

import type { AuthProvider } from '@refinedev/core'

type LoginParams =
  | { provider: 'GOOGLE' | 'APPLE'; idToken: string }
  | { provider: 'KAKAO' | 'NAVER'; code: string; redirectUri: string; state: string }

export const authProvider: AuthProvider = {
  login: async (params: LoginParams) => {
    try {
      const res =
        'code' in params
          ? await authService.loginWithSocial(params.provider, {
              type: 'CODE',
              code: params.code,
              redirectUri: params.redirectUri,
              state: params.state,
            })
          : await authService.loginWithSocial(params.provider, {
              type: 'TOKEN',
              token: params.idToken,
            })
      storeAuthData(res.accessToken, res.refreshToken, res.user, res.expiresIn)
      setAuthToken(res.accessToken)
      logger.info('[Auth] Dealer login successful:', { userId: res.user.userId })
      return { success: true, redirectTo: consumeLoginRedirectTo() }
    } catch (error_) {
      logger.error('[Auth] Dealer login error:', error_)
      // Not a BOLTB member or not a dealer — every login path (Kakao, Naver, Google, Apple) goes to
      // the dealer-access guide instead of a generic login error
      if (isDealerAccessDenied(error_)) return redirectToDealerAccess()
      const cause: unknown = error_ instanceof Error && 'cause' in error_ ? error_.cause : error_
      const msg =
        extractUserFacingMessage(cause) ??
        (error_ instanceof Error && error_.message === 'DEALER_ROLE_REQUIRED'
          ? i18n.t('common.errors.dealerRoleRequired')
          : error_ instanceof Error
            ? error_.message
            : i18n.t('common.errors.loginFailedMessage'))
      return { success: false, error: { name: 'LoginError', message: msg } }
    }
  },

  logout: async () => {
    // A super admin proxy tab (#273): 로그아웃 = 접속 종료 — the server stops the token at once.
    const wasProxySession = isProxySession()
    if (wasProxySession) await authService.endProxySession()
    clearAuthData()
    setAuthToken(null)
    return { success: true, redirectTo: wasProxySession ? PROXY_SESSION_ENDED_PATH : '/login' }
  },

  check: async () => {
    const token = getStoredToken()
    const refreshToken = getStoredRefreshToken()
    const expiresAt = getTokenExpiresAt()
    // An expired access token used to count as authenticated, which bounced /login back to /
    // and locked the dealer out of the app. Sessions stored before expiry was tracked have no
    // timestamp — treat those as still valid and let the API 401 handle them.
    const isAccessTokenUsable = !!token && (expiresAt === null || expiresAt > Date.now())

    if (token && isAccessTokenUsable) {
      setAuthToken(token)
      return { authenticated: true }
    }

    // A proxy session has no refresh token — once it is over the tab says so instead of a login page.
    if (isProxySession()) {
      clearAuthData()
      setAuthToken(null)
      return { authenticated: false, redirectTo: PROXY_SESSION_ENDED_PATH, logout: true }
    }

    if (refreshToken) {
      try {
        const response = await authService.refreshUserToken(refreshToken)
        const user = getStoredUser()
        if (user) {
          storeAuthData(response.accessToken, response.refreshToken, user, response.expiresIn)
        }
        setAuthToken(response.accessToken)
        return { authenticated: true }
      } catch {
        clearAuthData()
        localStorage.setItem('session_expired', 'true')
        return { authenticated: false, redirectTo: '/login', logout: true }
      }
    }

    clearAuthData()
    setAuthToken(null)
    return { authenticated: false, redirectTo: '/login', logout: true }
  },

  onError: (error: unknown) => {
    logger.error('[Auth] Error:', error)
    const statusCode = getErrorStatusCode(error)

    if (statusCode === 401 && isProxySession()) {
      return Promise.resolve({
        logout: true,
        redirectTo: PROXY_SESSION_ENDED_PATH,
        error: createHttpError(error, 401),
      })
    }

    if (statusCode === 401) {
      const currentPath = window.location.pathname + window.location.search
      localStorage.setItem('session_expired', 'true')
      return Promise.resolve({
        logout: true,
        redirectTo: `/login?to=${encodeURIComponent(currentPath)}`,
        error: createHttpError(error, 401),
      })
    }

    return Promise.resolve({ error: createHttpError(error, statusCode) })
  },

  getIdentity: () => {
    const user = getStoredUser()
    if (!user) return Promise.resolve(null)
    return Promise.resolve({
      userId: user.userId,
      nickname: user.nickname,
      profileImage: user.profileImage,
    })
  },

  getPermissions: () => {
    const user = getStoredUser()
    return Promise.resolve(user?.roles ?? null)
  },
}
